import {
  House, SquarePen, CalendarClock, Library, Activity, FolderKanban,
  MessageSquare, UserSearch, Bot, CircleHelp, LogOut, X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useKhoaGui } from '../services/khoaGui';

/**
 * Thanh điều hướng bên trái.
 *
 * Nhãn, thứ tự và biểu tượng lấy từ bản đang chạy trên https://fbmax.net,
 * đọc qua CDP ngày 14/09/2026. Lớp Tailwind cũng là nguyên văn từ DOM thật,
 * không phải viết lại theo cảm nhận.
 */

export type ManHinh =
  | 'tong-quan' | 'tao-bai' | 'lich-dinh-ky' | 'kho-bai-viet'
  | 'tien-trinh' | 'kenh-nhom' | 'binh-luan' | 'tim-kiem-kh' | 'mcp-agent' | 'tro-giup';

interface MucMenu {
  ma: ManHinh;
  nhan: string;
  icon: LucideIcon;
  sapCo?: boolean;
}

/**
 * QUY TAC: muc nao dan toi man `ChuaDung` (App.tsx:235) thi BAT BUOC co
 * `sapCo: true`.
 *
 * Vi sao: nhan "SAP CO" la loi hua duy nhat nguoi dung thay TRUOC khi bam.
 * Thieu no thi muc trong y het muc dung duoc — bam vao moi thay man trong.
 * Do dung 21/09/2026: 'kho-bai-viet' khong co nhan nhung van roi vao ChuaDung,
 * trong khi 'tim-kiem-kh' ngay ben canh thi co. Cung mot ket cuc, hai cach bao.
 *
 * Day la ho hang gan cua loi "nut AI Spin cam" da sua o dot 3: giao dien trong
 * nhu dung duoc nhung khong lam gi. Nguoi dung bam di bam lai roi ket luan
 * cong cu hong, thay vi biet la tinh nang chua lam xong.
 *
 * Khi dung mot man that, XOA `sapCo` VA them nhanh render o App.tsx — thieu mot
 * trong hai thi nhan va thuc te lai lech nhau lan nua.
 */
const MENU: MucMenu[] = [
  { ma: 'tong-quan',    nhan: 'Tổng quan',    icon: House },
  { ma: 'tao-bai',      nhan: 'Tạo bài',      icon: SquarePen },
  { ma: 'lich-dinh-ky', nhan: 'Lịch định kỳ', icon: CalendarClock },
  { ma: 'kho-bai-viet', nhan: 'Kho bài viết', icon: Library, sapCo: true },
  { ma: 'tien-trinh',   nhan: 'Tiến Trình',   icon: Activity },
  { ma: 'kenh-nhom',    nhan: 'Kênh & nhóm',  icon: FolderKanban },
  { ma: 'binh-luan',    nhan: 'Bình luận',    icon: MessageSquare },
  { ma: 'tim-kiem-kh',  nhan: 'Tìm kiếm KH',  icon: UserSearch },
];

// Lớp nguyên văn từ DOM thật — giữ để giao diện khớp bản cũ.
const MUC_CHUNG =
  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors';
const MUC_DANG_MO =
  'bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-400';
const MUC_THUONG =
  'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700/50 dark:hover:text-white';
const MUC_DUOI =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50';

interface Props {
  dangMo: ManHinh;
  doiMan: (m: ManHinh) => void;
  moTrenDienThoai: boolean;
  dongMenu: () => void;
  daDangNhap: boolean;
  /**
   * Phải trả Promise để thanh bên biết lúc nào việc xong mà mở khoá nút.
   * Trước đây kiểu là `() => void` nên không có cách nào chờ.
   */
  onDangXuat: () => Promise<void>;
}

/**
 * KHÔNG THÊM MỤC QUẢN TRỊ VÀO ĐÂY.
 *
 * Thanh bên này là không gian làm việc của KHÁCH HÀNG. Công cụ vận hành —
 * duyệt đơn, đổi mật khẩu người khác, sửa khuôn payload — nằm ở trang riêng
 * `/admin` (`public/admin.html`), có giao diện tách bạch hẳn.
 *
 * Ngày 22/09/2026 tôi từng nhét "Khuôn payload" vào đây. Sai: nó sửa được
 * payload của MỌI khách hàng mà lại nằm giữa "Tạo bài" và "Bình luận" — mời
 * gọi bấm nhầm, và trộn lẫn nơi khách dùng với nơi vận hành.
 */
export function ThanhBen({ dangMo, doiMan, moTrenDienThoai, dongMenu, daDangNhap, onDangXuat }: Props) {
  const [dangDangXuat, chayDangXuat] = useKhoaGui();
  return (
    <aside
      className={
        'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white ' +
        'transition-transform dark:border-slate-700 dark:bg-slate-800 md:static md:z-auto md:translate-x-0 ' +
        (moTrenDienThoai ? 'translate-x-0' : '-translate-x-full')
      }
    >
      {/* Đầu trang */}
      <div className="flex items-center gap-2 border-b border-slate-200 p-4 dark:border-slate-700">
        <a
          href="/"
          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-lg font-bold text-white">
            F
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-bold text-slate-900 dark:text-white">FBMax</span>
            <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
              FBI Social Engine
            </span>
          </span>
        </a>
        <button onClick={dongMenu} aria-label="Đóng menu" className="rounded-lg p-2 text-slate-400 md:hidden">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Menu chính */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Không gian làm việc
        </p>
        {MENU.map(({ ma, nhan, icon: Icon, sapCo }) => (
          <button
            key={ma}
            onClick={() => { doiMan(ma); dongMenu(); }}
            className={MUC_CHUNG + ' ' + (dangMo === ma ? MUC_DANG_MO : MUC_THUONG)}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 text-left">{nhan}</span>
            {sapCo && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                SẮP CÓ
              </span>
            )}
          </button>
        ))}
      </nav>

      {/* Chân trang */}
      <div className="space-y-1 border-t border-slate-200 p-3 dark:border-slate-700">
        <a href="/" className={MUC_DUOI + ' font-semibold'}>
          <House className="h-[18px] w-[18px]" />
          Về trang chủ
        </a>
        <button
          onClick={() => { doiMan('mcp-agent'); dongMenu(); }}
          className={MUC_DUOI + ' w-full ' + (dangMo === 'mcp-agent' ? MUC_DANG_MO : '')}
        >
          <Bot className="h-[18px] w-[18px]" />
          MCP Agent
        </button>
        <button
          onClick={() => { doiMan('tro-giup'); dongMenu(); }}
          className={MUC_DUOI + ' w-full ' + (dangMo === 'tro-giup' ? MUC_DANG_MO : '')}
        >
          <CircleHelp className="h-[18px] w-[18px]" />
          Trung tâm trợ giúp
        </button>
        {daDangNhap ? (
          <button
            onClick={() => chayDangXuat(onDangXuat)}
            disabled={dangDangXuat}
            aria-busy={dangDangXuat || undefined}
            className={MUC_DUOI + ' text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60 dark:text-rose-400'}
          >
            {dangDangXuat
              ? <span aria-hidden="true" className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-current border-t-transparent" />
              : <LogOut className="h-[18px] w-[18px]" />}
            {dangDangXuat ? 'Đang đăng xuất...' : 'Đăng xuất'}
          </button>
        ) : (
          <a href="/login?next=%2Fapp%2F" className={MUC_DUOI + ' font-semibold text-slate-700 dark:text-slate-200'}>
            <LogOut className="h-[18px] w-[18px]" />
            Đăng nhập
          </a>
        )}
      </div>
    </aside>
  );
}

export { MENU };
